/**
 * Where a spoken turn's time goes, from the creator's side: ms after they stopped talking until each step
 * (transcribed, sent, the call's voice first said something, the agent's first words arrived, the first audio
 * was requested / arrived / started playing). One timeline per turn, posted to `POST /voice/timing` once the
 * reply has started playing (or the turn is over), and read back with the server's own marks in the latency
 * report (backend `app/harness/voice.py`).
 */
import type { VoiceTimingBody } from '@/agent/api'

export type TimelineMark =
    | 'speech_end' | 'transcript' | 'send_start' | 'sent' | 'first_say' | 'first_text'
    | 'first_tts_request' | 'first_audio' | 'playback'
    | 'talker_audio' | 'delegated' | 'relay_audio'

export interface TimelineMeta {
    pipeline: VoiceTimingBody['pipeline']
    stt?: VoiceTimingBody['stt']
    talker?: VoiceTimingBody['talker']
}

export class TurnTimeline {
    readonly marks: Partial<Record<TimelineMark, number>> = {}
    runId: string | null = null
    steered = false
    private posted = false

    constructor(private readonly meta: TimelineMeta, private readonly t0 = performance.now(),
                private readonly now: () => number = () => performance.now()) {
        this.marks.speech_end = 0
    }

    /** The first time `name` happens in this turn (later ones are ignored). */
    mark(name: TimelineMark): void {
        if (this.marks[name] === undefined) this.marks[name] = Math.round(this.now() - this.t0)
    }

    get done(): boolean {
        return this.posted
    }

    /** The body to post, once (null if already posted or the turn never reached a run). */
    take(): VoiceTimingBody | null {
        if (this.posted || !this.runId) return null
        this.posted = true
        return { run_id: this.runId, marks: { ...this.marks } as Record<string, number>, ...this.meta, ...(this.steered ? { steered: true } : {}) }
    }
}
